import { defineStore } from "pinia";
import { usePlanStore } from "~/stores/PlanStore";
import { useObserverStore } from "~/stores/ObserverStore";
import type { TalkUserData } from "~/entities/TalkUserData";
import type { ITalkRoomRepository } from "~/interfaces/ITalkRoomRepository";
import { TalkRoomRepository } from "~/repositories/TalkRoomRepository";

export type TalkUnreadType = "home" | "thread" | "mention";

export const useTalkUnreadStore = defineStore("TalkUnreadStore", () => {
  // タブ単位の未読フラグ (ブックマークは未読あり得ないので除外)
  const homeTabUnread = ref(false); // ホーム
  const threadTabUnread = ref(false); // スレッド
  const mentionTabUnread = ref(false); // メンション

  // データごとの未読フラグを管理
  // key: データID, value: 未読フラグ
  // home の key は talkRoomId, thread の key は threadTalkId, mention の key は talkId
  const homeUnreads: Record<string, boolean> = {};
  const threadUnreads: Record<string, boolean> = {};
  const mentionUnreads: Record<string, boolean> = {};

  const planStore = usePlanStore();
  const observerStore = useObserverStore();

  const repository: ITalkRoomRepository = new TalkRoomRepository();

  // 講座初期化
  const initPlan = () => {
    homeTabUnread.value = false;
    threadTabUnread.value = false;
    mentionTabUnread.value = false;
    Object.keys(homeUnreads).forEach(key => delete homeUnreads[key]);
    Object.keys(threadUnreads).forEach(key => delete threadUnreads[key]);
    Object.keys(mentionUnreads).forEach(key => delete mentionUnreads[key]);
  };

  // 講座トークタブへ未読フラグを設定する
  const setPlanTalkUnread = () => {
    if (!planStore.plan) {
      return;
    }
    planStore.plan.hasUnreadTalk = homeTabUnread.value || threadTabUnread.value || mentionTabUnread.value;
  };

  // ホームタブへ未読フラグを設定する
  const setHomeTabUnread = () => {
    // ひとつ以上が true なら未読
    homeTabUnread.value = Object.values(homeUnreads).some(flag => flag);
    // 講座ページのトークタブの未読フラグも設定する
    setPlanTalkUnread();
  };

  // スレッドタブへ未読フラグを設定する
  const setThreadTabUnread = () => {
    // ひとつ以上が true なら未読
    threadTabUnread.value = Object.values(threadUnreads).some(flag => flag);
    // 講座ページのトークタブの未読フラグも設定する
    setPlanTalkUnread();
  };

  // メンションタブへ未読フラグを設定する
  const setMentionTabUnread = () => {
    // ひとつ以上が true なら未読
    mentionTabUnread.value = Object.values(mentionUnreads).some(flag => flag);
    // 講座ページのトークタブの未読フラグも設定する
    setPlanTalkUnread();
  };

  // type = home | thread | mention に対して未読・既読状態を設定する
  // unreadId は type ごとに異なる
  // - home: talkRoomId
  // - thread: threadTalkId
  // - mention: talkId
  const setUnread = (type: TalkUnreadType, unreadId: string, unreadFlag: boolean) => {
    switch (type) {
      case "home":
        homeUnreads[unreadId] = unreadFlag;
        setHomeTabUnread();
        break;
      case "thread":
        threadUnreads[unreadId] = unreadFlag;
        setThreadTabUnread();
        break;
      case "mention":
        mentionUnreads[unreadId] = unreadFlag;
        setMentionTabUnread();
        break;
    }
  };

  // type = home | thread | mention の未読・既読状態をクリアする
  // unreadId は type ごとに異なる
  // - home: talkRoomId
  // - thread: threadTalkId
  // - mention: talkId
  const removeUnread = (type: TalkUnreadType, unreadId: string) => {
    switch (type) {
      case "home":
        delete homeUnreads[unreadId];
        setHomeTabUnread();
        break;
      case "thread":
        delete threadUnreads[unreadId];
        setThreadTabUnread();
        break;
      case "mention":
        delete mentionUnreads[unreadId];
        setMentionTabUnread();
        break;
    }
  };

  // 以下、スレッド・メンション未読監視用の関数
  // ホーム＝トークルームの未読監視用関数は TalkRoomStore にあるのでここでは定義しない

  // スレッド未読監視開始
  const startTalkThreadsUnreadObserve = (planId: string, userId: string) => {
    const callback = (userData: TalkUserData, type: "added" | "modified" | "removed") => {
      if (type === "added" || type === "modified") {
        setUnread("thread", userData.talk.talkId, userData.hasUnread);
      } else if (type === "removed") {
        removeUnread("thread", userData.talk.talkId);
      }
    };
    observerStore.add("talkThreadsUnread", {
      start: () => repository.startTalkThreadsUnreadObserve(planId, userId, callback),
      stop: () => repository.stopTalkThreadsUnreadObserve(),
    });
  };

  // スレッド未読監視停止
  const stopTalkThreadsUnreadObserve = () => {
    observerStore.remove("talkThreadsUnread");
  };

  // メンション未読監視開始
  const startTalkMentionsUnreadObserve = (planId: string, userId: string) => {
    const callback = (userData: TalkUserData, type: "added" | "modified" | "removed") => {
      if (type === "added" || type === "modified") {
        setUnread("mention", userData.talk.talkId, userData.hasUnread);
      } else if (type === "removed") {
        removeUnread("mention", userData.talk.talkId);
      }
    };
    observerStore.add("talkMentionsUnread", {
      start: () => repository.startTalkMentionsUnreadObserve(planId, userId, callback),
      stop: () => repository.stopTalkMentionsUnreadObserve(),
    });
  };

  // メンション未読監視停止
  const stopTalkMentionsUnreadObserve = () => {
    observerStore.remove("talkMentionsUnread");
  };

  return {
    homeTabUnread,
    threadTabUnread,
    mentionTabUnread,
    initPlan,
    setUnread,
    removeUnread,
    startTalkThreadsUnreadObserve,
    stopTalkThreadsUnreadObserve,
    startTalkMentionsUnreadObserve,
    stopTalkMentionsUnreadObserve,
  };
});
